// Magnetic Tape Equalization Calculator
// Original program by Jay McKnight, Magnetic Reference Laboratory, Inc.
// Web version © 2025

// Standard speeds and equalizations data
const standards = [
    { speed: 0.0,     flo: 0.0,  fhi: Infinity, label: "Constant Flux" },
    { speed: 47.675,  flo: 50.0, fhi: 1327.0,   label: "1.88 in/s, IEC (1981) OpenReel" },
    { speed: 95.25,   flo: 50.0, fhi: 1768.0,   label: "3.75 in/s, NAB & IEC Equalization" },
    { speed: 95.25,   flo: 50.0, fhi: 1327.0,   label: "3.75 in/s, obsolete EIA RS-224 (1959) Eq" },
    { speed: 95.25,   flo: 50.0, fhi: 796.0,    label: "3.75 in/s, obsolete Ampex (1953) Eq" },
    { speed: 127.0,   flo: 50.0, fhi: 2170.0,   label: "5 in/s, Spcl w/ 50 & 2240 Hz Trans freqs" },
    { speed: 190.5,   flo: 0.0,  fhi: 2274.0,   label: "7.5 in/s, IEC (IEC1) Equalization" },
    { speed: 190.5,   flo: 0.0,  fhi: 1592.0,   label: "7.5 in/s, obsolete CCIR (1953) Eq" },
    { speed: 190.5,   flo: 50.0, fhi: 3183.0,   label: "7.5 in/s, NAB (IEC2) Equalization" },
    { speed: 190.5,   flo: 0.0,  fhi: 3183.0,   label: "7.5 in/s, NAB & IEC Broadcast Cart Eq" },
    { speed: 381.0,   flo: 0.0,  fhi: 4547.0,   label: "15 in/s, IEC (IEC1) Equalization" },
    { speed: 381.0,   flo: 50.0, fhi: 3183.0,   label: "15 in/s, NAB (IEC2) Equalization" },
    { speed: 381.0,   flo: 0.0,  fhi: 6366.0,   label: "15 in/s, Proposed Studio Master Eq" },
    { speed: 381.0,   flo: 50.0, fhi: 11789.0,  label: "15 in/s, Nagra Master Equalization" },
    { speed: 762.0,   flo: 0.0,  fhi: 9095.0,   label: "30 in/s, AES (IEC2) Equalization" },
    { speed: 762.0,   flo: 0.0,  fhi: 4547.0,   label: "30 in/s, obsolete IEC1 Equalization" },
    { speed: 762.0,   flo: 0.0,  fhi: Infinity, label: "30 in/s, obsolete Ampex 200A & 300 Eq" },
    { speed: 762.0,   flo: 0.0,  fhi: 5895.0,   label: "30 in/s, obsolete DGG Equalization" },
    { speed: 762.0,   flo: 0.0,  fhi: 6366.0,   label: "30 in/s, approx obsolete DGG Equalization" }
];

// MRL Standard frequencies (as used on calibration tapes)
const mrlFreqs = [32, 63, 125, 250, 500, 1000, 2000, 4000, 8000, 10000, 12500, 16000, 20000];

// Calculate complex magnitude: sqrt(a^2 + b^2)
function zmag(a, b) {
    return Math.sqrt(a * a + b * b);
}

// Calculate calibration tape flux response ratio
function rcaltape(freq, fclo, fchi) {
    const lowTerm = zmag(fclo / freq, 1.0);
    const highTerm = zmag(freq / fchi, 1.0);
    return lowTerm / highTerm;
}

// Calculate playback flux response ratio
function rplay(freq, fplo, fphi) {
    const highTerm = zmag(freq / fphi, 1.0);
    const lowTerm = zmag(fplo / freq, 1.0);
    return highTerm / lowTerm;
}

// Convert voltage ratio to dB
function voltageRatioToDb(ratio) {
    return 20.0 * Math.log10(ratio);
}

/**
 * Calculate conversion table data for given calibration and playback standards
 * @param {number} calIndex - Index of calibration standard
 * @param {number} playIndex - Index of playback standard
 * @param {Array} frequencies - Array of frequencies to calculate
 * @returns {Object|null} Conversion data object or null if indices are equal
 */
function calculateConversionData(calIndex, playIndex, frequencies = mrlFreqs) {
    // Return null if configurations are the same
    if (calIndex === playIndex) {
        return null;
    }

    const calStd = standards[calIndex];
    const playStd = standards[playIndex];

    // Calculate frequency ratio
    let freqRatio;
    if (playStd.speed === 0.0 || calStd.speed === 0.0) {
        freqRatio = 1.0;
    } else {
        freqRatio = playStd.speed / calStd.speed;
    }

    // Calculate 1000 Hz reference response
    const ref1kHz = rcaltape(1000, calStd.flo, calStd.fhi) *
        rplay(1000 * freqRatio, playStd.flo, playStd.fhi);

    // Calculate conversion data for each frequency
    const conversions = frequencies.map(calFreq => {
        const playFreq = Math.round(calFreq * freqRatio);
        const response = rcaltape(calFreq, calStd.flo, calStd.fhi) *
            rplay(calFreq * freqRatio, playStd.flo, playStd.fhi);
        const normalizedResponse = response / ref1kHz;
        const levelDb = voltageRatioToDb(normalizedResponse);

        return {
            recordedFreq: calFreq,
            playbackFreq: playFreq,
            levelDb: levelDb,
            isReference: (calFreq === 1000)
        };
    });

    return {
        calibrationStandard: calStd,
        playbackStandard: playStd,
        frequencyRatio: freqRatio,
        conversions: conversions
    };
}

// ============================================================================
// UI HANDLING
// ============================================================================

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', function() {

    // Get references to DOM elements
    const calTapeSelect = document.getElementById('calTape');
    const playbackSelect = document.getElementById('playback');
    const resultsDiv = document.getElementById('results');

    // Function to update the conversion table
    function updateTable() {
        const calIndex = parseInt(calTapeSelect.value);
        const playIndex = parseInt(playbackSelect.value);

        // Clear results if same selection
        if (calIndex === playIndex) {
            resultsDiv.innerHTML = '<div class="no-results">Please select different calibration and playback settings.</div>';
            return;
        }

        // Calculate conversion data
        const data = calculateConversionData(calIndex, playIndex);

        if (!data) {
            resultsDiv.innerHTML = '<div class="no-results">Unable to calculate conversion. Please try different settings.</div>';
            return;
        }

        // Build the results HTML
        let html = '';

        // Add caption with configuration info
        html += '<div class="table-caption">';
        html += '<strong>Conversion Configuration:</strong>';
        html += `Calibration: ${data.calibrationStandard.label}<br>`;
        html += `Playback: ${data.playbackStandard.label}<br>`;
        html += `Frequency Ratio: ${data.frequencyRatio.toFixed(2)}`;
        html += '</div>';

        // Add scroll indicator for mobile
        html += '<div class="scroll-indicator">← Scroll horizontally to see all columns →</div>';

        // Build the table
        html += '<div class="table-container">';
        html += '<table class="results-table">';
        html += '<thead><tr>';
        html += '<th>Recorded Frequency [Hz]</th>';
        html += '<th>Playback Frequency [Hz]</th>';
        html += '<th>Level [dB]</th>';
        html += '</tr></thead>';
        html += '<tbody>';

        // Add each conversion row
        data.conversions.forEach(function(conv) {
            const rowClass = conv.isReference ? ' class="highlight-row"' : '';
            html += `<tr${rowClass}>`;
            html += `<td>${conv.recordedFreq}</td>`;
            html += `<td>${conv.playbackFreq}</td>`;
            html += `<td>${conv.levelDb.toFixed(2)}</td>`;
            html += '</tr>';
        });

        html += '</tbody>';
        html += '</table>';
        html += '</div>';

        // Update the results div
        resultsDiv.innerHTML = html;
    }

    // Add event listeners to both select elements
    calTapeSelect.addEventListener('change', updateTable);
    playbackSelect.addEventListener('change', updateTable);

    // Generate initial table on page load
    updateTable();
});
